"use client";

import { useCallback, useEffect, useState } from "react";
import { useStudio } from "@/contexts/studio-context";

export interface PianoPressSetState {
  /** MIDIs of `expected` that have been sounded at least once. */
  hit: ReadonlySet<number>;
  complete: boolean;
  reset: () => void;
}

/**
 * Progress through an *unordered* set of notes, the way a chord is played.
 *
 * `usePianoPressSequence` is the ordered sibling and the wrong tool here: a
 * chord's notes land within a few milliseconds of each other in whatever order
 * the fingers happen to fall, and asking for an order would fail a player who
 * played the chord correctly. Notes accumulate rather than having to sound
 * together, so a hand that cannot yet hold all three at once still gets
 * through — the shape is what is being learnt, not the simultaneity.
 */
export function usePianoPressSet(
  expected: readonly number[],
  options: { enabled?: boolean } = {},
): PianoPressSetState {
  const { enabled = true } = options;
  const { studio } = useStudio();
  const [hit, setHit] = useState<ReadonlySet<number>>(() => new Set());
  const [tracked, setTracked] = useState(expected);

  // Same render-time reset as usePianoPressSequence: a new target set starts
  // from nothing, and doing it in an effect would leave one frame showing the
  // previous chord's progress.
  if (tracked !== expected) {
    setTracked(expected);
    setHit(new Set());
  }

  const reset = useCallback(() => setHit(new Set()), []);

  useEffect(() => {
    if (!enabled || !studio || expected.length === 0) {
      return;
    }

    const wanted = new Set(expected);
    const piano = studio.getPiano();

    return piano.on((event) => {
      if (event.type !== "press") {
        return;
      }

      const midi = event.note.toMidi();

      if (!wanted.has(midi)) {
        return;
      }

      setHit((prev) => {
        if (prev.has(midi)) {
          return prev;
        }

        const next = new Set(prev);
        next.add(midi);

        return next;
      });
    });
  }, [enabled, studio, expected]);

  return {
    hit,
    complete: expected.length > 0 && hit.size >= new Set(expected).size,
    reset,
  };
}
